import { Head, router } from "@inertiajs/react"
import { Phone } from "lucide-react"
import { useState } from "react"

import {
  type A2pPrefill,
  A2pRegistrationSheet,
} from "@/components/agents/a2p-registration-sheet"
import HeadingSmall from "@/components/heading-small"
import { PhoneNumberCard } from "@/components/phone-number-card"
import { Button } from "@/components/ui/button"
import { Empty } from "@/components/ui/empty"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { cn } from "@/lib/utils"
import { settingsIntegrationsPath, settingsSmsRegistrationPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Settings", href: settingsIntegrationsPath() },
]

// The account's A2P 10DLC registration state, mapped from
// accounts.sms_registration_status. Only "unregistered" offers an action —
// once submitted, the page is informational until carriers approve.
type SmsStatus = "unregistered" | "pending" | "approved"

const A2P_STATES: Record<
  SmsStatus,
  { name: string; dotClass: string; subtitle: string }
> = {
  unregistered: {
    name: "Not started",
    dotClass: "bg-muted-foreground/40",
    subtitle: "Complete registration to enable your agents to send texts.",
  },
  pending: {
    name: "In review",
    dotClass: "bg-amber-500",
    subtitle: "Under carrier review, which usually takes 1 to 3 business days.",
  },
  approved: {
    name: "Registered",
    dotClass: "bg-green-500",
    subtitle: "Your agents can send and receive texts.",
  },
}

// IMP-391: each agent owns its own number, so this renders one card per
// number, labelled with the agent it belongs to. Accounts provisioned before
// the split may still share one number — those render a single card naming
// both agents.
export interface PhoneNumberAssignment {
  number: string
  agents: string[]
}

function PhoneNumberSection({
  phoneNumbers,
}: {
  phoneNumbers: PhoneNumberAssignment[]
}) {
  // No heading of its own: with texting pulled this is the page's only
  // section, so "Phone" → "Phone number" was a heading nested under a heading
  // saying the same thing. Restore a label here when A2PSection returns and
  // there are two sections to tell apart.
  return (
    <section className="space-y-3">
      {phoneNumbers.map((assignment) => (
        <PhoneNumberCard
          key={assignment.number}
          phoneNumber={assignment.number}
          agents={assignment.agents}
        />
      ))}
    </section>
  )
}

function A2PSection({
  status,
  prefill,
}: {
  status: SmsStatus
  prefill: A2pPrefill
}) {
  const [registerOpen, setRegisterOpen] = useState(false)
  const state = A2P_STATES[status]

  return (
    <section>
      <div className="mb-3">
        <h4 className="text-sm font-medium">A2P 10DLC registration</h4>
        <p className="text-muted-foreground text-sm">
          US carriers require this before your agents can send text messages.
        </p>
      </div>
      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-block h-2 w-2 rounded-full",
                  state.dotClass,
                )}
              />
              <span className="text-sm font-medium">{state.name}</span>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
              {state.subtitle}
            </p>
          </div>
          {status === "unregistered" && (
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              onClick={() => setRegisterOpen(true)}
            >
              Register
            </Button>
          )}
        </div>
      </div>

      <A2pRegistrationSheet
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        prefill={prefill}
        onSubmit={(payload) => {
          router.post(
            settingsSmsRegistrationPath(),
            { sms_registration: payload },
            { preserveScroll: true },
          )
          setRegisterOpen(false)
        }}
      />
    </section>
  )
}

export default function PhoneSettings({
  phone_numbers,
  sms_registration,
  // IMP-423: single gate that hides the A2P 10DLC section while texting is
  // pulled from self-serve. Flip LEAD_RESPONDER_TEXTING_ENABLED to restore.
  texting_enabled,
}: {
  phone_numbers: PhoneNumberAssignment[]
  sms_registration: { status: SmsStatus; prefill: A2pPrefill }
  texting_enabled: boolean
}) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Phone" />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Phone"
            description={
              texting_enabled
                ? "The phone numbers your assistant uses for calls and texts, and your SMS compliance status. Each job gets its own number, assigned automatically when activated."
                : "The phone numbers your assistant uses for calls. Each job gets its own number, assigned automatically when activated."
            }
          />
          {/* IMP-387: A2P registration is meaningless without a number, so
              before one is provisioned the whole section collapses to a single
              empty state — no number cards, no A2P block.
              IMP-423: the A2P block is also hidden while texting is pulled. */}
          {phone_numbers.length > 0 ? (
            <>
              <PhoneNumberSection phoneNumbers={phone_numbers} />
              {texting_enabled && (
                <A2PSection
                  status={sms_registration.status}
                  prefill={sms_registration.prefill}
                />
              )}
            </>
          ) : (
            <div className="rounded-lg border">
              <Empty
                icon={<Phone className="size-4" />}
                title="No phone number yet"
                description={
                  texting_enabled
                    ? "A phone number is assigned automatically when you activate a job that makes calls or sends texts."
                    : "A phone number is assigned automatically when you activate a job that makes calls."
                }
              />
            </div>
          )}
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
